import React from 'react';
import { CircledExclamationPointIcon, LoaderIcon, XIcon } from '@components/Icons';
import cn from 'classnames';
import LoginPanel, {
  LoginBox,
  LoginFooterLinks,
  LoginPanelWrapper,
} from '@components/Login/LoginPanel';
import ViewMessage from '@components/ViewMessage';
import { supportHref } from '@support/preferred-email-client';
import LoginView from '@views/Auth/components/LoginView';
import chatgptLogo from '@app/img/login/logos/chatgpt.svg';
import notionLogo from '@app/img/login/logos/notion.svg';
import clayLogo from '@app/img/login/logos/clay.svg';
import styles from './OauthLoginViews.module.scss';
import { useAuthContext } from '@support/hooks/use-auth-context';
import Pill from '@components/Pill';
import Contact from '@models/Contact';
import { LoginButton } from '@components/Login/Buttons';

type Integration = 'notion' | 'chatgpt';

const labelByIntegration: Record<Integration, string> = {
  notion: 'Notion',
  chatgpt: 'ChatGPT',
};

const connectMessageByIntegration: Record<Integration, string> = {
  notion: 'Authorize Clay to send contacts to your Notion.',
  chatgpt: 'Authorize ChatGPT to have read-only access to your Clay contacts.',
};

export function LoginToClay({ integration }: { integration: Integration }) {
  const copy = (
    <>
      Connect your email, calendar, LinkedIn
      <br />
      and more to import all your relationships and
      <br />
      use the Clay {labelByIntegration[integration]} Plugin.
    </>
  );

  return (
    <LoginView>
      <LoginPanel
        userMessage="Login to continue"
        userSignupMessage="Create your Clay account"
        copyAfterMessage={copy}
        copyAfterSignupMessage={copy}
      />
    </LoginView>
  );
}

export function NoCredentialsError({ integration }: { integration: Integration }) {
  return (
    <ViewMessage
      headline="Invalid Request"
      message={
        <p>
          Please go back and try again,
          <br />
          or{' '}
          <a
            href={supportHref({
              subject: `Issue logging in with ${labelByIntegration[integration]}`,
            })}
          >
            reach out to us
          </a>{' '}
          if you need assistance.
        </p>
      }
      icon={<CircledExclamationPointIcon />}
      button={{
        text: 'Go to Clay',
        onClick: () => (location.href = '/'),
      }}
      underHeader
    />
  );
}

const IntegrationGraphic = ({ integration }: { integration: Integration }) => {
  return (
    <div className={styles.integrationGraphicContainer}>
      <img
        src={integration === 'chatgpt' ? chatgptLogo : notionLogo}
        alt={`${labelByIntegration[integration]} Logo`}
        className={styles.logo}
      />
      <XIcon color="textPrimary" style={{ opacity: 0.32 }} />
      <img src={clayLogo} alt="Clay Logo" className={styles.logo} />
    </div>
  );
};

const UserPill = () => {
  const { user } = useAuthContext();

  if (!user) return null;

  let displayName = `${user.first_name} ${user.last_name}`;

  if (!displayName.trim()) {
    displayName = user.email;
  }

  if (!displayName) {
    return null;
  }

  return (
    <Pill
      className={styles.pill}
      contact={
        {
          displayName: `${user.first_name} ${user.last_name}`,
          avatarURL: user.avatar_url,
        } as Contact
      }
    />
  );
};

const OAuthView = ({ children }: { children: React.ReactNode }) => (
  <LoginPanelWrapper>
    <UserPill />
    {children}
    <LoginFooterLinks className={styles.footerLinks} />
  </LoginPanelWrapper>
);

export function LoginError({ integration }: { integration: Integration }) {
  return (
    <OAuthView>
      <LoginBox className={styles.box}>
        <IntegrationGraphic integration={integration} />
        <div className={styles.headline}>{labelByIntegration[integration]} Connection Failed</div>
        <div className={styles.message}>
          An error occurred. Please try again or{' '}
          <a href={supportHref({ subject: `Issue with ${labelByIntegration[integration]} Login` })}>
            reach out to us
          </a>{' '}
          if you need assistance.
        </div>
      </LoginBox>
    </OAuthView>
  );
}

export function AllowAccess({
  integration,
  onAllow,
}: {
  integration: Integration;
  onAllow: () => void;
}) {
  return (
    <OAuthView>
      <LoginBox className={cn(styles.box, styles.withBtns)}>
        <IntegrationGraphic integration={integration} />
        <div className={styles.headline}>Connect {labelByIntegration[integration]} to Clay</div>
        <div className={styles.message}>{connectMessageByIntegration[integration]}</div>
        <LoginButton onClick={onAllow} className={cn(styles.allowBtn, styles.btn)}>
          Authorize
        </LoginButton>
        <button className={cn(styles.cancelBtn, styles.btn)} onClick={() => (location.href = '/')}>
          Cancel
        </button>
      </LoginBox>
    </OAuthView>
  );
}

export function FinishingAuth({ integration }: { integration: Integration }) {
  return (
    <OAuthView>
      <LoginBox className={cn(styles.box, styles.withBtns)}>
        <IntegrationGraphic integration={integration} />
        <div className={styles.headline}>Connect {labelByIntegration[integration]} to Clay</div>
        <div className={styles.message}>
          Wrapping up authorizing with {labelByIntegration[integration]}. One moment please.
        </div>
        <LoginButton className={cn(styles.allowBtn, styles.btn)} disabled>
          <LoaderIcon customColor="#fff" width="18" height="18" r="1" />
        </LoginButton>
        <button
          className={cn(styles.cancelBtn, styles.btn)}
          onClick={() => (location.href = '/')}
          disabled
        >
          Cancel
        </button>
      </LoginBox>
    </OAuthView>
  );
}
